<template>
    <div class="metric-set-container">
        <button id="metricsSetModal" @click="showCustomizeMetricsModal" v-if="metricSetsForAdUnit.length < 2">
            <IconBase class="customize-icon"
                      width="14"
                      height="17"
                      display="inline-flex"
                      iconName="customizeIcon"></IconBase>
            <span>Customize Metrics</span>
        </button>
        <Dropdown v-else
                  uniqueId="metricSetDropdown"
                  :options="metricSetsDropdownOptions"
                  :selectedId="selectedMetricSetId || 'default'"
                  height="34"
                  selectedDisplayTextField="titleText"
                  :searchEnabled="false"
                  markSelected
                  @dropdownSelectedItemChanged="selectedMetricSetChanged">
            <template slot="preOption" slot-scope="slotData">
                <IconBase v-if="slotData.option.action"
                          display="inline-flex"
                          class="customize-icon"
                          width="14"
                          height="17"
                          iconName="customizeIcon"></IconBase>
            </template>
        </Dropdown>
    </div>
</template>

<script>
import IconBase from 'components/IconBase.vue';
import CustomizeMetricSetModal from 'monetize/reports/components/metricSet/CustomizeMetricSetModal.vue';
import { adUnitsMap } from 'services/utils/adUnitsMap';
import Dropdown from 'components/dropdowns/Dropdown.vue';
import http from 'services/utils/http';
import { mapActions } from 'vuex';

export default {
    name: 'MetricSet',
    components: { IconBase, Dropdown },
    props: {
        metricSets: {
            type: Object,
            required: true
        },
        selectedIndex: {
            type: Number,
            required: true
        },
        adUnit: {
            type: [Number, String],
            required: true
        },
        availableKpis: {
            type: Object,
            required: true
        }
    },
    computed: {
        adUnitName() {
            return adUnitsMap[this.adUnit].displayName;
        },
        metricSetsForAdUnit() {
            return this.metricSets[this.adUnit];
        },
        sortedMetricSetsForAdUnit() {
            return [...this.metricSetsForAdUnit].sort(this.sortByDefault);
        },
        selectedMetricSetId() {
            return this.metricSetsForAdUnit[this.selectedIndex].id;
        },
        metricSetsDropdownOptions() {
            return [
                ...this.sortedMetricSetsForAdUnit.map((metricSet) => ({
                    id: metricSet.id,
                    titleText: metricSet.name,
                    displayText: metricSet.name
                })),
                {
                    id: 'action',
                    titleText: 'Customize Metrics',
                    displayText: 'Customize Metrics',
                    action: this.showCustomizeMetricsModal
                }
            ];
        }
    },
    methods: {
        ...mapActions({
            getWithCache: 'cache/getWithCache'
        }),
        showCustomizeMetricsModal() {
            this.$modal.open({
                title: `Customize Metrics - ${this.adUnitName}`,
                props: {
                    metricSets: this.sortedMetricSetsForAdUnit,
                    availableKpis: this.availableKpis[this.adUnit].defaultBreakDown
                },
                version: 1,
                clickOutsideClose: false,
                okButtonText: 'Save',
                onOK: async ({ metricSets }) => {
                    try {
                        await http.put('/api/rest/v1/partners/MetricSet', {
                            reportType: 'bidding',
                            metricSets,
                            deliveryGroup: this.adUnit
                        });

                        const { data: metricSetsResponse } = await this.getWithCache({
                            api: 'biddingReport',
                            req: '/api/rest/v1/partners/MetricSet?reportType=bidding',
                            refresh: true
                        });
                        this.$emit('metricSetsSaved', metricSetsResponse);
                    } catch (error) {
                        this.$emit('metricSetsSaveError', error);
                    }
                },
                component: CustomizeMetricSetModal
            });
        },
        selectedMetricSetChanged({ id }) {
            this.$emit('update:selectedIndex', this.metricSetsForAdUnit.findIndex((ms) => ms.id === id));
        },
        sortByDefault(a, b) {
            if (a.isDefault) {
                return -1;
            }

            if (b.isDefault) {
                return 1;
            }

            return a - b;
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.V2";
@import "~styles/buttons.scss";

.customize-icon {
    margin-right: 3px;
    vertical-align: sub;
}

.metric-set-container {
    width: 170px;
    margin-top: -4px;

    & ::v-deep li {
        &.action .preset-element {
            padding-left: 16px;
            padding-top: 2px;

            &:hover {
                background-color: $white;
            }
        }

        .customize-icon + .truncate {
            display: inline-flex;
        }
    }
}
</style>
